import { cloneElement, isValidElement, useId } from "react";
import type {
  InputHTMLAttributes,
  ReactNode,
  Ref,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "./cn";

/**
 * The shared control surface. Every input, select and textarea uses it, so a field looks the same
 * wherever it appears and a focus ring never differs between two forms.
 */
const BASE =
  "w-full rounded-lg border border-ink-200 bg-surface px-3 py-2 text-sm text-ink-900 " +
  "placeholder:text-ink-400 transition-colors " +
  "focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 " +
  "disabled:bg-ink-50 disabled:text-ink-400 disabled:cursor-not-allowed";

export function Label({
  children,
  required,
  htmlFor,
}: {
  children: ReactNode;
  required?: boolean;
  htmlFor?: string;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-ink-700">
      {children}
      {/* red-600, not red-500: the lighter shade measures 3.8:1 against the form ground, and
          this asterisk is the only thing saying the field is required. */}
      {required && <span className="ml-0.5 text-red-600">*</span>}
    </label>
  );
}

/**
 * Wraps a control with its label, hint and error.
 *
 * <p>The label is bound to the control by id. Without that binding a screen reader announces an
 * unlabelled input, clicking the label does not focus the field, and `getByLabelText` finds
 * nothing — which is how the end-to-end suite discovered this was missing.
 *
 * <p>The id is generated here and cloned onto the child, so no caller has to invent one and no
 * two fields can collide. A child that already carries an id keeps it.
 *
 * <p>The error replaces the hint rather than appearing alongside it. Showing both stacks two
 * competing messages under one field, and the hint is advice the reader no longer needs once
 * something is actually wrong.
 */
export function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label?: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  const generatedId = useId();
  const messageId = `${generatedId}-message`;

  const control = isValidElement<{ id?: string; "aria-describedby"?: string }>(children)
    ? cloneElement(children, {
        id: children.props.id ?? generatedId,
        "aria-describedby": hint || error ? messageId : undefined,
      })
    : children;

  return (
    <div>
      {label && (
        <Label required={required} htmlFor={generatedId}>
          {label}
        </Label>
      )}
      {control}
      {error ? (
        <p id={messageId} className="mt-1 text-xs text-red-600">
          {error}
        </p>
      ) : (
        // ink-600 rather than ink-400. Measured at 3:1 the hints were below AA, and they are
        // not decoration — "2h 30m, 2:30, 150m or 2.5" is how somebody learns the field.
        hint && (
          <p id={messageId} className="mt-1 text-xs text-ink-600">
            {hint}
          </p>
        )
      )}
    </div>
  );
}

/**
 * `ref` is declared explicitly. React 19 passes it as an ordinary prop, but the DOM prop types do
 * not include it, so without this a caller cannot focus the field.
 */
export function Input({
  className,
  ref,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> }) {
  return <input ref={ref} className={cn(BASE, className)} {...rest} />;
}

/**
 * A textarea, which may be handed a ref.
 *
 * <p>React 19 passes `ref` to a function component like any other prop, so this needs no
 * forwardRef wrapper — it only has to say in its type that it accepts one. A control whose caret
 * position matters, like the mention box, cannot work without reaching the element.
 */
export function Textarea({
  className,
  ref,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  ref?: Ref<HTMLTextAreaElement>;
}) {
  return <textarea ref={ref} className={cn(BASE, "resize-y", className)} {...rest} />;
}

/**
 * A select with its own chevron: the native arrow differs across platforms and looks foreign.
 *
 * <p>The wrapper exists only to position that chevron, so every prop — id and aria-describedby
 * included — is forwarded to the `select` itself. Leaving them on the wrapper would break the
 * label association Field sets up.
 */
export function Select({
  className,
  children,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={cn(BASE, "appearance-none pr-9", className)} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
    </div>
  );
}
